import usePermissions from "@/hooks/use-permissions.hook.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiEmployee, getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Avatar,
  DotSeparator,
  Stack,
  Tag,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export const HiredTag = ({ employee }: { employee: ApiEmployee }) => {
  const { t } = useTranslation();
  const { renderDate } = useFormatter();
  const { canIView } = usePermissions();
  const modalsService = useModals();

  const canOpenHiredEmployeeProfile = canIView(ApiPermission.see_employee_directory);

  const hiredInfo = useMemo(() => {
    const hiredDateRaw = employee.date_hired_on;
    let hiredDateText: string | undefined;
    if (hiredDateRaw) {
      const hiredDate = new Date(hiredDateRaw);
      if (!Number.isNaN(hiredDate.getTime())) {
        hiredDateText = renderDate(hiredDate);
      }
    }

    const hiredPosition = getEmployeePositionTitle(employee);
    const hiredCompanyRaw = employee.employee_position?.department?.title ?? "";

    const hiredRoleText = hiredPosition.trim().length > 0 ? hiredPosition : undefined;
    const hiredCompany = hiredCompanyRaw.trim().length > 0 ? hiredCompanyRaw : undefined;
    const hiredAvatarUrl = employee.avatar_url
      ? getEmployeeAvatarUrl(
          {
            id: employee.id,
            first_name: employee.first_name,
            last_name: employee.last_name,
            middle_name: employee.middle_name ?? undefined,
            avatar_url: employee.avatar_url,
          },
          "sm"
        )
      : undefined;

    return {
      avatarUrl: hiredAvatarUrl,
      hiredDateText,
      hiredRoleText,
      hiredCompany,
    };
  }, [employee, renderDate]);

  return (
    <Tooltip>
      <TooltipTrigger>
        <Tag
          variant={"positive"}
          appearance={"filled"}
          className={twMerge("flex gap-1", canOpenHiredEmployeeProfile && "cursor-pointer")}
          onClick={() => {
            if (!canOpenHiredEmployeeProfile) return;
            modalsService.openModal(EmployeeViewModal, {
              employeeId: employee.id,
            });
          }}
        >
          <Avatar size={"sm"} url={hiredInfo.avatarUrl} />
          {t("core:lifecycle_status_hired")}
        </Tag>
      </TooltipTrigger>
      <TooltipContent>
        <Stack gap={"xs"}>
          {hiredInfo.hiredDateText && (
            <Text className={"text-white text-center"}>
              {`${t("core:hired_on")}: ${hiredInfo.hiredDateText}`}
            </Text>
          )}
          {(hiredInfo.hiredRoleText || hiredInfo.hiredCompany) && (
            <DotSeparator
              className="inline-flex items-center justify-center text-white"
              dotClassName="text-white"
            >
              {hiredInfo.hiredRoleText && (
                <Text className={"text-white text-center"}>{hiredInfo.hiredRoleText}</Text>
              )}
              {hiredInfo.hiredCompany && (
                <Text className={"text-white text-center"}>{hiredInfo.hiredCompany}</Text>
              )}
            </DotSeparator>
          )}
        </Stack>
      </TooltipContent>
    </Tooltip>
  );
};
